(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileViewport = window.matchMedia('(max-width: 767px)');

  const waitForPoster = (banner) => {
    const poster = banner.querySelector('[data-kickass-banner-poster] img');

    if (!poster) return Promise.resolve();

    if (poster.complete) {
      return typeof poster.decode === 'function'
        ? poster.decode().catch(() => {})
        : Promise.resolve();
    }

    return new Promise((resolve) => {
      poster.addEventListener('load', resolve, { once: true });
      poster.addEventListener('error', resolve, { once: true });
    });
  };

  const attachSources = (banner, video) => {
    if (video.dataset.sourcesAttached === 'true') return;

    const template = banner.querySelector(
      `template[data-kickass-video-sources="${video.id}"]`,
    );

    if (!template) return;

    video.appendChild(template.content.cloneNode(true));
    video.dataset.sourcesAttached = 'true';
    video.preload = 'auto';
    video.load();
  };

  const detachSources = (video) => {
    if (video.dataset.sourcesAttached !== 'true') return;

    video.pause();
    video.classList.remove('is-playing');
    video.querySelectorAll('source').forEach((source) => source.remove());
    delete video.dataset.sourcesAttached;
    video.preload = 'none';
    video.load();
  };

  const selectVideo = (videos) => {
    const viewport = mobileViewport.matches ? 'mobile' : 'desktop';

    return (
      videos.find((video) => video.dataset.viewport === viewport) ||
      videos.find((video) => !video.dataset.viewport)
    );
  };

  const initBanner = (banner) => {
    if (banner.dataset.videoInitialized === 'true') return;

    const videos = Array.from(
      banner.querySelectorAll('[data-kickass-banner-video]'),
    );

    if (!videos.length) return;

    banner.dataset.videoInitialized = 'true';

    let isNearViewport = false;
    let posterReady = false;

    const updateVideo = () => {
      const selectedVideo = selectVideo(videos);

      videos.forEach((video) => {
        if (video !== selectedVideo) detachSources(video);
      });

      if (
        !selectedVideo ||
        !posterReady ||
        !isNearViewport ||
        reducedMotion.matches ||
        document.hidden
      ) {
        selectedVideo?.pause();
        return;
      }

      attachSources(banner, selectedVideo);

      const playPromise = selectedVideo.play();
      if (playPromise) playPromise.catch(() => {});
    };

    const handlePlaying = (event) => {
      event.currentTarget.classList.add('is-playing');
    };

    const handleVisibility = () => updateVideo();
    const handleMediaChange = () => updateVideo();

    videos.forEach((video) => {
      video.addEventListener('playing', handlePlaying);
    });

    let observer;

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        ([entry]) => {
          isNearViewport = Boolean(entry?.isIntersecting);
          updateVideo();
        },
        { rootMargin: '200px 0px', threshold: 0 },
      );
      observer.observe(banner);
    } else {
      isNearViewport = true;
    }
    reducedMotion.addEventListener('change', handleMediaChange);
    mobileViewport.addEventListener('change', handleMediaChange);
    document.addEventListener('visibilitychange', handleVisibility);

    waitForPoster(banner).then(() => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          posterReady = true;
          updateVideo();
        });
      });
    });

    banner.cleanupKickassVideo = () => {
      observer?.disconnect();
      reducedMotion.removeEventListener('change', handleMediaChange);
      mobileViewport.removeEventListener('change', handleMediaChange);
      document.removeEventListener('visibilitychange', handleVisibility);
      videos.forEach((video) => {
        video.removeEventListener('playing', handlePlaying);
        detachSources(video);
      });
      delete banner.dataset.videoInitialized;
    };
  };

  const init = (scope = document) => {
    const banners = scope.matches?.('[data-section-type="kickass-collection-banner"]')
      ? [scope]
      : scope.querySelectorAll('[data-section-type="kickass-collection-banner"]');

    banners.forEach(initBanner);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const banner = event.target.querySelector(
      '[data-section-type="kickass-collection-banner"]',
    );
    banner?.cleanupKickassVideo?.();
  });
})();
